import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {PrlContactUsService} from '../../services/contact-us/prl-contact-us.service';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';
import {
    MxButtonModel,
    MxDynamicFormService,
    MxFormCustomizerEvent,
    MxNotificationService,
    OnBoFormReady,
    UiDataObj
} from 'mx-core';
import {combineLatestAll, of, Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {
    MxPreloaderService,
    withPreloader
} from "../../../../../../../mx-core/src/lib/modules/mx-preloader/services/mx-preloader.service";
import {PrlValidationManagerService} from "../../../../shared/services/prl-validation-manager.service";
import {PrlCaptchaFieldService} from '../../../../shared/services/prl-captcha-field.service';

@Component({
    selector: 'prl-contact-us-page',
    templateUrl: './prl-contact-us-page.component.html',
    standalone: false,
    styleUrls: ['./prl-contact-us-page.component.scss']
})
export class PrlContactUsPageComponent implements OnInit, OnDestroy, OnBoFormReady {
    private _destroy$$: Subject<null> = new Subject<null>();

    private form: FormGroup = null;
    sendButton: MxButtonModel;

    protected usingCaptcha: boolean;
    protected captchaKey: string;

    constructor(private readonly formBuilder: FormBuilder,
                private readonly prlContactUsService: PrlContactUsService,
                private readonly dynamicFormService: MxDynamicFormService,
                private readonly notificationService: MxNotificationService,
                private readonly preloader: MxPreloaderService,
                private readonly languageService: PrlLanguageService,
                private readonly validationManagerService: PrlValidationManagerService,
                private readonly captchaFieldService: PrlCaptchaFieldService)
    {
        validationManagerService.init().subscribe();
    }

    ngOnInit(): void {
        this.sendButton = {
            label: () => this.getLabel('label.send'),
            permission: 'all',
            tooltip: () => this.getLabel('label.send'),
            className: 'mxs-button--primary',
            disabled: () => !this.form || this.form.invalid,
            clickHandler: this.sendEmail
        };
    }

    onCustomize(event: MxFormCustomizerEvent): void {
        event.fieldsData.next({
            objectMetaSource: null,
            initialData: {}
        });
        event
            .customizeFormMode('new')
            .customizeBoFormConfig({title: 'label.contact.us'})
            .customizeActions({
                submit: {text: 'label.submit', visible: false},
                cancel: {text: 'label.cancel', visible: false}
            })
            .customizeProps({addPrefixToTitle: false, showNavigation: false})
            .customizeFieldConfigs('message', {style: 'triple-width-column'})
            .customizeFieldConfigs('name', {style: 'half-width-column'})
            .customizeFieldConfigs('email', {style: 'half-width-column'})
            .customizeFieldConfigs('recaptcha', {style: 'triple-width-column'});

        of(
            this.dynamicFormService.getUIData('com.playtech.mexos.contract.portal.ContactUsObj', false, true),
            this.captchaFieldService.isUsingCaptcha(),
            this.captchaFieldService.getCaptchaKey()
        ).pipe(takeUntil(this._destroy$$), withPreloader(this.preloader), combineLatestAll())
            .subscribe((response) => {
                let fieldSource: UiDataObj = response[0] as UiDataObj;
                this.usingCaptcha = Boolean(response[1]);
                if (this.usingCaptcha) {
                    this.captchaKey = response[2] as string;
                }

                fieldSource.fieldsData.forEach(value => {
                    if (value.name === 'message' && this.usingCaptcha) {
                        this.captchaFieldService.injectCaptchaField(fieldSource.fieldsData, value);
                    }
                });

                event.fieldsData.next({
                    objectMetaSource: fieldSource,
                    initialData: {}
                });
            });
        event.addCustomizer(this);
    }

    boOnFormReady(form: FormGroup): void {
        this.form = form;
    }

    resolved(event: string): void {
        this.captchaFieldService.resolve(this.form, event);
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    getLabel(label: string): string {
        return this.languageService.getLabel(label);
    }

    sendEmail = () => {
        this.notificationService.notify(
            this.prlContactUsService.sendMessage(this.form.value),
            'Message was sent',
            'Error while sending message')
            .subscribe(() => {
                this.form.reset();
            });
        this.prlContactUsService.sendMessage(this.form.value);
    }
}
